import { AiUsageRecordRow } from "../types/MeGenTypes";

import {
  AiModelRate,
  AiRateDim,
  AiRateUnit,
  OPEN_END_DATE,
  PriceBook,
  ProviderDefault,
  RateOverrides,
  entryKeyOf,
  expectedVariantsOf,
  isVariantKeyForUnit,
  localDayOf,
  providerDefaultKeyOf,
  rateKeyOf,
  todayDay,
  variantSortKey,
  windowContains,
} from "./aiPriceBook";
import { PRICE_BOOK, inferUnit, providerDefaultOf, resolveRate, variantLabelOf, variantOf } from "./aiModelRates";
import { lineOfRecord } from "./aiModelCost";
import { CLOUD_BUCKETS, aggregateRecords, toLocalDay } from "./aiUsagePivot";
import { usageModelOf } from "./aiUsageModels";

/**
 * Helpers (pure) ของแท็บ "ราคา Model" — ช่อง = 1 variant
 * แถว = 1 model ที่อยู่ใน price book / 1 ค่าเริ่มต้นราย provider / **1 (model × unit) สำหรับ model นอก book**
 * (model นอก book เดา unit จาก usage จึงมีได้หลายแถวต่อ 1 model แต่ override ยังลงที่ key เดียว)
 * ลำดับแถวคงที่ตาม price book (ไม่เรียงตาม cost → พิมพ์ราคาแล้วแถวไม่ขยับ)
 */

export type RateCardField = {
  variant: string; // "720P" | "1080P/noaudio" | "image" | "in" | "out" | "hour" | "all" | "call"
  label: string; // ป้ายหน้าช่อง ("720P", "เข้า", "" เมื่อมีช่องเดียว)
  defaultPrice: number | undefined; // จาก price book (ฟรี → 0); undefined = ยังไม่ได้ตั้งราคาในไฟล์
  price: number | undefined; // override ?? defaultPrice
  overridden: boolean;
  stale: boolean; // override variant ที่ไม่ตรง unit ปัจจุบัน (price book เปลี่ยน unit) — ไม่ใช้ ไม่ export
};

export type RateCardRowKind = "model" | "provider_default" | "unknown";

export type RateCardRow = {
  kind: RateCardRowKind;
  key: string; // override key: provider|model หรือ provider|*
  entryKey: string; // key ของ override รายการนี้ — รายการมีวันที่ต่อท้าย @start~end (React key ใช้อันนี้)
  provider: string;
  model: string; // provider_default → "*"
  service: string;
  unit: AiRateUnit | null; // null = provider default ที่ฟรีไม่ระบุ unit → ไม่มีช่อง
  free: boolean;
  fields: RateCardField[];
  overriddenCount: number;
  source?: string;
  note?: string;
  startDate?: string; // ช่วงวันที่ของรายการ (undefined = ใช้ได้ทุกวัน)
  endDate?: string;
  isCurrent?: boolean; // ช่วงที่มีผลวันนี้ (model เดียวกันมีได้หลายช่วง)
  // kind === "unknown" เท่านั้น
  fromProviderDefault?: boolean;
  calls?: number;
  lastUsed?: string | null;
};

export type RateCardGroup = { provider: string; label: string; rows: RateCardRow[] };

// ลำดับ provider ในตาราง = ลำดับเดียวกับ bucket ของ dashboard (เพิ่ม provider ที่ CLOUD_BUCKETS ที่เดียว)
const PROVIDER_ORDER = CLOUD_BUCKETS.flatMap((b) => b.providers);
export const DEFAULTS_GROUP = "_defaults";

/**
 * price book ถูกอัปเดตหลังจากที่ผู้ใช้แก้ราคาใน browser นี้หรือยัง (ใช้ขึ้นแบนเนอร์เตือน)
 *
 * `savedAt` เป็น ISO ที่มี timezone (UTC) แต่ `book.updated` เป็น **วันตามปฏิทิน** ที่คนเขียนไฟล์กรอกไว้
 * จึงต้องแปลง savedAt เป็นวันท้องถิ่นก่อนเทียบ ไม่ใช่ตัด 10 ตัวแรกของ ISO
 * (ไม่งั้นคนที่แก้ราคาก่อน 07:00 ตามเวลาไทย จะเห็นแบนเนอร์เตือนทั้งที่เพิ่งแก้ไปเมื่อกี้)
 */
export const isBookNewerThanEdits = (
  savedAt: string | null | undefined,
  bookUpdated: string | null | undefined
): boolean => {
  if (!savedAt || !bookUpdated) return false;
  const editedDay = toLocalDay(savedAt);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(editedDay)) return false; // อ่านวันไม่ออก → ไม่เตือน
  return editedDay < bookUpdated;
};

export const providerLabelOf = (provider: string): string => {
  const bucket = CLOUD_BUCKETS.find((b) => b.providers.includes(provider));
  return bucket ? `${bucket.label} (${provider})` : provider;
};

// ป้ายช่วงวันที่ของรายการ — "2025-01-01 → 2026-12-31" / "2027-01-01 → ถาวร" / "ตั้งแต่ 2025-01-01" / "ถึง 2026-12-31"
export const dateRangeLabelOf = (startDate?: string, endDate?: string): string => {
  const end = endDate === OPEN_END_DATE ? "ถาวร" : endDate;
  if (startDate && end) return `${startDate} → ${end}`;
  if (startDate) return `ตั้งแต่ ${startDate}`;
  return `ถึง ${end || ""}`;
};

// ป้ายหน้าช่อง: token → เข้า/ออก, second → "720P" / "720P · ไม่มีเสียง" / "ทุก resolution", ช่องเดียว → ""
export const fieldLabelOf = (unit: AiRateUnit, variant: string, single: boolean): string => {
  if (unit === "token_1m") return variant === "in" ? "เข้า" : "ออก";
  if (unit === "second") {
    const label = variantLabelOf(variant);
    if (label) return label;
    return single ? "" : "ทุก resolution";
  }
  return "";
};

const buildFields = (
  unit: AiRateUnit,
  dims: AiRateDim[] | undefined,
  defaults: Record<string, number>,
  ov: Record<string, number> | undefined,
  free: boolean
): RateCardField[] => {
  const keys = new Set<string>([...Object.keys(defaults), ...Object.keys(ov || {})]);
  const variants = keys.size ? Array.from(keys) : expectedVariantsOf(unit, dims);
  variants.sort((a, b) => variantSortKey(a) - variantSortKey(b) || (a < b ? -1 : 1));
  const single = variants.length === 1;
  return variants.map((variant) => {
    const defaultPrice = defaults[variant] !== undefined ? defaults[variant] : free ? 0 : undefined;
    const overridden = ov?.[variant] !== undefined;
    return {
      variant,
      label: fieldLabelOf(unit, variant, single),
      defaultPrice,
      price: overridden ? ov![variant] : defaultPrice,
      overridden,
      stale: overridden && !isVariantKeyForUnit(unit, variant),
    };
  });
};

const countOverridden = (fields: RateCardField[]): number => fields.filter((f) => f.overridden).length;

const rowOfModel = (m: AiModelRate, overrides: RateOverrides): RateCardRow => {
  const entryKey = entryKeyOf(m); // override ของรายการ dated ผูกกับช่วงของมันเอง
  const fields = buildFields(m.unit, m.dims, m.prices, overrides[entryKey], !!m.free);
  return {
    kind: "model",
    key: m.key,
    entryKey,
    provider: m.provider,
    model: m.model,
    service: m.service,
    unit: m.unit,
    free: !!m.free,
    fields,
    overriddenCount: countOverridden(fields),
    source: m.source,
    note: m.note,
    startDate: m.startDate,
    endDate: m.endDate,
    isCurrent: (m.startDate !== undefined || m.endDate !== undefined) && windowContains(m, todayDay()),
  };
};

const rowOfProviderDefault = (def: ProviderDefault, overrides: RateOverrides): RateCardRow => {
  const key = providerDefaultKeyOf(def.provider);
  const fields = def.unit ? buildFields(def.unit, def.dims, def.prices, overrides[key], def.free) : [];
  return {
    kind: "provider_default",
    key,
    entryKey: key,
    provider: def.provider,
    model: "*",
    service: def.service || "",
    unit: def.unit || null,
    free: def.free,
    fields,
    overriddenCount: countOverridden(fields),
    note: def.note,
  };
};

const providerSort = (a: string, b: string): number => {
  const ia = PROVIDER_ORDER.indexOf(a);
  const ib = PROVIDER_ORDER.indexOf(b);
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return a < b ? -1 : 1;
};

export const buildRateCardRows = (book: PriceBook, overrides: RateOverrides): RateCardGroup[] => {
  const byProvider = new Map<string, RateCardRow[]>();
  book.models.forEach((m) => {
    const p = m.provider.toLowerCase();
    if (!byProvider.has(p)) byProvider.set(p, []);
    byProvider.get(p)!.push(rowOfModel(m, overrides));
  });

  const groups: RateCardGroup[] = Array.from(byProvider.keys())
    .sort(providerSort)
    .map((provider) => ({ provider, label: providerLabelOf(provider), rows: byProvider.get(provider)! }));

  const defaultRows = Object.values(book.providerDefaults)
    .sort((a, b) => providerSort(a.provider, b.provider))
    .map((def) => rowOfProviderDefault(def, overrides));
  if (defaultRows.length) {
    groups.push({ provider: DEFAULTS_GROUP, label: "ค่าเริ่มต้นของ provider (model ที่ไม่มีในรายการด้านบน)", rows: defaultRows });
  }
  return groups;
};

// model ที่พบใน records แต่ไม่มีใน price book (rate.inferred) — snapshot/alias ของ model ใน book ไม่นับ
export const unknownModelsFromRecords = (
  records: AiUsageRecordRow[],
  overrides: RateOverrides,
  book: PriceBook = PRICE_BOOK
): RateCardRow[] => {
  // resolve rate ต่อ record ด้วย cache key เดียวกับ cost engine (provider|model|unit-ที่เดา|วันที่)
  // เพราะ model นอก price book เดา unit จาก usage ของแต่ละแถว → model เดียวอาจมีได้หลาย unit
  // (เช่น record แรก error → เดาเป็น "call" แต่แถวถัดไปมี token → "token_1m")
  const rateCache = new Map<string, AiModelRate>();
  const groups = new Map<
    string,
    { key: string; unit: AiRateUnit; base: AiModelRate; first: AiUsageRecordRow; variants: Set<string>; rows: AiUsageRecordRow[] }
  >();

  records.forEach((r) => {
    const line = lineOfRecord(r);
    const key = rateKeyOf(r.provider, usageModelOf(r));
    const cacheKey = `${key}|${inferUnit(line)}|${localDayOf(line.at) || ""}`;
    let base = rateCache.get(cacheKey);
    if (!base) {
      base = resolveRate(line, {}, book).rate;
      rateCache.set(cacheKey, base);
    }
    if (!base.inferred) return; // มีใน price book (หรือ alias/snapshot) → ไม่ใช่ model นอก book

    const groupKey = `${key}|${base.unit}`;
    let group = groups.get(groupKey);
    if (!group) {
      group = { key, unit: base.unit, base, first: r, variants: new Set<string>(), rows: [] };
      groups.set(groupKey, group);
    }
    group.rows.push(r);

    const { variant } = variantOf(group.base, line);
    if (variant === "tokens") {
      group.variants.add("in");
      group.variants.add("out");
    } else group.variants.add(variant);
  });

  if (!groups.size) return [];

  const aggByGroup = new Map<string, ReturnType<typeof aggregateRecords>[number]>();
  groups.forEach((group, groupKey) => {
    const [agg] = aggregateRecords(group.rows, () => groupKey);
    if (agg) aggByGroup.set(groupKey, agg);
  });

  const rows = Array.from(groups.entries())
    .map(([groupKey, group]): RateCardRow => {
      const { key, unit, base, first } = group;
      const ov = overrides[key];
      const providerOv = overrides[providerDefaultKeyOf(base.provider)] || {};
      const defaults = { ...base.prices, ...providerOv };
      // เอาเฉพาะ variant ที่เป็นของ unit นี้ ไม่ให้ราคาของอีก unit (เช่น in/out) หลุดมาโผล่ในแถว call
      const belongs = (variant: string) => isVariantKeyForUnit(unit, variant);
      const keys = new Set<string>([
        ...Array.from(group.variants),
        ...Object.keys(defaults).filter(belongs),
        ...Object.keys(ov || {}).filter(belongs),
      ]);
      const variants = Array.from(keys).sort((a, b) => variantSortKey(a) - variantSortKey(b) || (a < b ? -1 : 1));
      const single = variants.length === 1;
      const fields: RateCardField[] = variants.map((variant) => {
        const defaultPrice = defaults[variant] !== undefined ? defaults[variant] : base.free ? 0 : undefined;
        const overridden = ov?.[variant] !== undefined;
        return {
          variant,
          label: fieldLabelOf(unit, variant, single),
          defaultPrice,
          price: overridden ? ov![variant] : defaultPrice,
          overridden,
          stale: overridden && !belongs(variant),
        };
      });
      const agg = aggByGroup.get(groupKey);
      return {
        kind: "unknown",
        key,
        entryKey: key,
        provider: first.provider,
        model: usageModelOf(first),
        service: base.service || first.service,
        unit,
        free: !!base.free,
        fields,
        overriddenCount: countOverridden(fields),
        note: base.note,
        fromProviderDefault: !!providerDefaultOf(first.provider, book),
        calls: agg?.calls || 0,
        lastUsed: agg?.last_used || null,
      };
    })
    .sort(
      (a, b) =>
        (b.calls || 0) - (a.calls || 0) ||
        (a.key < b.key ? -1 : a.key > b.key ? 1 : 0) ||
        ((a.unit || "") < (b.unit || "") ? -1 : 1)
    );

  // override ที่ไม่ตรงกับ unit ของแถวไหนเลยของ key นั้น (เช่น เคยกรอกตอน log ยังเดา unit เป็นอย่างอื่น)
  // ต้องยังมองเห็นและกดคืนค่าได้ ไม่งั้นจะเป็นราคาที่ค้างอยู่โดยไม่มีใครเห็น
  const shownByKey = new Map<string, Set<string>>();
  rows.forEach((row) => {
    const set = shownByKey.get(row.key) || new Set<string>();
    row.fields.forEach((f) => set.add(f.variant));
    shownByKey.set(row.key, set);
  });
  const firstRowOfKey = new Map<string, RateCardRow>();
  rows.forEach((row) => {
    if (!firstRowOfKey.has(row.key)) firstRowOfKey.set(row.key, row);
  });
  firstRowOfKey.forEach((row, key) => {
    const shown = shownByKey.get(key) || new Set<string>();
    const leftovers = Object.keys(overrides[key] || {}).filter((variant) => !shown.has(variant));
    if (!leftovers.length) return;
    row.fields = [
      ...row.fields,
      ...leftovers.sort((a, b) => variantSortKey(a) - variantSortKey(b) || (a < b ? -1 : 1)).map((variant) => ({
        variant,
        // ไม่มีป้าย — variant พวกนี้ไม่ใช่ของ unit ของแถวนี้ ถ้าส่งเข้า fieldLabelOf จะได้ป้ายผิดความหมาย
        // (เช่น แถว token ได้ "ออก" ซ้ำสองช่อง, แถว second ได้ "ทุก resolution" ให้ variant in/out)
        // UI โหมด stale แสดงชื่อ variant อยู่แล้ว จึงไม่ต้องมีป้ายกำกับ
        label: "",
        defaultPrice: undefined,
        price: overrides[key][variant],
        overridden: true,
        stale: true, // ไม่ตรงกับ unit ที่ log ใช้จริง → ไม่ถูกนำไปคิดเงินและไม่ถูก export
      })),
    ];
    row.overriddenCount = countOverridden(row.fields);
  });

  return rows;
};

/**
 * เลือกแถวเดียวต่อ 1 model สำหรับ export ไปเป็น entry ใน price book
 *
 * `unknownModelsFromRecords` อาจคืนหลายแถวต่อ model (แถวละ unit) แต่ entry ใน price book
 * มี unit เดียวต่อ model → ตัดสินตามลำดับนี้
 * 1. แถวที่ผู้ใช้กรอกราคาไว้ (มี field `overridden` ที่ไม่ `stale`) ชนะเสมอ
 * 2. unit อื่นชนะ `call` (ซึ่งมักเกิดจาก record ที่ error/ไม่มี usage)
 * 3. เสมอกันจึงดูจำนวน call มากกว่าชนะ
 */
export const pickExportRows = (rows: RateCardRow[]): RateCardRow[] => {
  const best = new Map<string, RateCardRow>();
  rows.forEach((row) => {
    const current = best.get(row.key);
    if (!current) {
      best.set(row.key, row);
      return;
    }
    // 1) แถวที่ผู้ใช้กรอกราคาไว้ต้องชนะเสมอ ไม่งั้นราคาที่กรอกจะไม่ถูก export เลย
    const usable = (r: RateCardRow) => r.fields.some((f) => f.overridden && !f.stale);
    const currentPriced = usable(current);
    const rowPriced = usable(row);
    if (currentPriced !== rowPriced) {
      if (rowPriced) best.set(row.key, row);
      return;
    }
    // 2) unit อื่นชนะ "call" (ซึ่งมักมาจาก record ที่ error/ไม่มี usage)
    const currentIsCall = current.unit === "call";
    const rowIsCall = row.unit === "call";
    if (currentIsCall !== rowIsCall) {
      if (currentIsCall) best.set(row.key, row);
      return;
    }
    // 3) สุดท้ายดูจำนวน call
    if ((row.calls || 0) > (current.calls || 0)) best.set(row.key, row);
  });
  return Array.from(best.values());
};

// override key ที่ไม่มีทั้งใน price book และใน log ที่โหลดอยู่ (model ถูกลบ/เปลี่ยนชื่อ) — โชว์ให้ลบได้
// (รวม plain key เก่าของ model ที่กลายเป็นรายการ dated — entryKey ของแถวเหล่านั้นมี suffix @start~end)
export const orphanOverrideKeys = (overrides: RateOverrides, knownKeys: Set<string>): string[] =>
  Object.keys(overrides)
    .filter((k) => !knownKeys.has(k))
    .sort();

export const knownKeysOf = (groups: RateCardGroup[], unknown: RateCardRow[]): Set<string> => {
  const keys = new Set<string>();
  groups.forEach((g) => g.rows.forEach((r) => keys.add(r.entryKey)));
  unknown.forEach((r) => keys.add(r.entryKey));
  return keys;
};
